// apps/client/src/hooks/useBrokerTargets.ts
// Real-time Firestore listener for broker performance targets.
// Also fetches current progress from BigQuery via callable.

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { normalizeBonusTiers, type BonusTier } from '../utils/bonusTiers';

export type { BonusTier } from '../utils/bonusTiers';
export type CommissionComponent = 'upfront' | 'trail';

// Current creatable types. Legacy types (upfront_commission, trail_commission)
// still appear in the type union because targets created before 2026-04-19
// may still exist in Firestore — the backend normalizes them to
// commission_earned + component on read, but snapshot listeners see the
// raw doc field.
export type TargetType =
  | 'loan_book_value'
  | 'settlement_volume'
  | 'commission_earned'
  // Legacy v2 types — no longer creatable, read-only migration path:
  | 'upfront_commission'
  | 'trail_commission';

export interface PerformanceTarget {
  id: string;
  type: TargetType;
  label: string;
  /** Mirrors bonusTiers[0].thresholdCents for legacy read compat. New code
   *  should consume `bonusTiers` directly — this field is the lowest tier's
   *  threshold and a back-compat shim only. */
  thresholdCents: number;
  /** Mirrors the highest tier's bonus on the snapshot (pre-evaluation).
   *  After progress evaluation, the cloud function returns the payable bonus
   *  from the highest met tier in `TargetProgress.bonusCents`. */
  bonusCents: number;
  /** Scaled bonus tiers — sorted ascending by thresholdCents, length ≥ 1.
   *  Always populated by the snapshot mapper (auto-migrated from legacy
   *  thresholdCents/bonusCents when the doc lacks bonusTiers). */
  bonusTiers: BonusTier[];
  period: 'monthly' | 'quarterly';
  isActive: boolean;
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  /** Present when type === 'commission_earned'. Picks which commission
   *  components the threshold sums: ['upfront'], ['trail'], or both. */
  commissionComponents?: CommissionComponent[];
  /** Optional loan book scoping — filters measure to one loan book. */
  loanBookId?: string;
  loanBookLabel?: string;
  loanBookTemplateId?: string;
  loanBookEntityCode?: string;
  /** YYYY-MM activation month. Present on targets created with v4+ schema
   *  (2026-04-19 onward). Absent on legacy targets — backend treats absent
   *  as always-active for backwards compat. */
  effectiveFromMonth?: string;
  /** Subject binding. When set, the target measures the recipient's share of
   *  the broker's measured commission (not the broker's own). Mutually
   *  exclusive at the wire (backend rejects both set).
   *  - assistantUserId: links to a broker_assistant uid + their sharePct in
   *    commission_config.assistantSplits.
   *  - analystId (PR 1A-v3): links to a credit_analyst id +
   *    upfrontSharePct/trailSharePct in commission_config.creditAnalystSplits.
   *    PR 1A-v4 (2026-05-14): analyst-bound targets ALSO filter by per-loan
   *    creditAnalystId tags, so the analyst measure only counts loans
   *    explicitly tagged to them. */
  assistantUserId?: string;
  assistantDisplayName?: string;
  analystId?: string;
  analystDisplayName?: string;
  /** PR 1A-v5.1 (2026-05-15): when this target was sourced from another
   *  broker's subcollection via the cross-broker analyst-target listener
   *  (e.g. Hannah Ruby's target lives under Andrew but is also visible on
   *  Kelsey's tab because Hannah is allocated on Kelsey too), this records
   *  the funding broker's doc id. Absent on targets owned by this broker.
   *  UI uses this to render a "Shared from {broker}" badge and to disable
   *  edit/delete (those operate on the funding broker's doc). */
  _crossBrokerSource?: string;
}

export interface TargetProgress {
  targetId: string;
  type: string;
  label: string;
  thresholdCents: number;
  bonusCents: number;
  /** Same as PerformanceTarget.bonusTiers — included so the UI can render the
   *  tier list + stepped progress markers without joining back to the target. */
  bonusTiers: BonusTier[];
  /** Index of the highest met tier in bonusTiers, or -1 if none met. */
  metTierIndex: number;
  /** Index of the next (unmet) tier, or -1 if all tiers met. */
  nextTierIndex: number;
  period: string;
  currentValueCents: number;
  isMet: boolean;
  percentComplete: number;
  commissionComponents?: CommissionComponent[];
  periodLabel?: string;
  loanBookId?: string;
  loanBookLabel?: string;
  loanBookTemplateId?: string;
  loanBookEntityCode?: string;
  /** YYYY-MM activation month (v4+ targets). */
  effectiveFromMonth?: string;
  /** TRUE when the current period ends before effectiveFromMonth. UI renders
   *  a "Starts {month}" badge instead of a progress bar. */
  notYetActive?: boolean;
  /** Subject binding — same as PerformanceTarget; surfaced on progress
   *  so the UI can render "Hanah (Credit Analyst)" badges next to the label. */
  assistantUserId?: string;
  assistantDisplayName?: string;
  analystId?: string;
  analystDisplayName?: string;
  /** Per-payment-type share pct lookup from the broker's commission_config
   *  at evaluation time. Present on assistant/analyst targets. */
  assistantSharePct?: number;
  analystUpfrontSharePct?: number;
  analystTrailSharePct?: number;
  analystSharePct?: number;
  /** PR 1A-v5 (2026-05-14): when an analyst-bound target measures progress
   *  across multiple brokers, the backend reports the full list of brokers
   *  contributing — each with that broker's own per-payment-type splits.
   *  Absent (undefined) when there's only one allocated broker (single-broker
   *  view is the per-broker number anyway). UI renders a "Combined: A + B"
   *  badge so admin can see the target spans multiple brokers without
   *  having to drill into each broker's tab. */
  combinedBrokers?: Array<{
    brokerDocId: string;
    brokerName: string;
    upfrontSharePct: number;
    trailSharePct: number;
  }>;
  /** PR 1A-v5.1 (2026-05-15): funding broker doc id when this progress
   *  entry corresponds to a cross-broker analyst target. Mirrors the
   *  field on PerformanceTarget so the UI can render the badge whether
   *  it's reading from `targets` or `progress`. */
  _crossBrokerSource?: string;
}

interface UseBrokerTargetsReturn {
  targets: PerformanceTarget[];
  progress: TargetProgress[];
  totalBonusPayableCents: number;
  loading: boolean;
  progressLoading: boolean;
  error: string | null;
  refetchProgress: () => void;
  /** 'YYYY-MM' the period targets were measured over (or the quarter holding
   *  it). The tenant's newest commission month unless `periodMonth` was set. */
  measuredMonth: string | null;
  /** Tenant's newest commission period_month, or null when unknown. */
  latestDataMonth: string | null;
  /** Set when the progress call failed; progress is cleared rather than
   *  left showing the previous month's numbers. */
  progressError: string | null;
}

/**
 * Real-time listener on `companies/{companyId}/brokers/{brokerDocId}/performance_targets`
 * filtered to active targets only.
 *
 * Also fetches progress from BigQuery via `getBrokerTargetProgress` callable.
 * `periodMonth` ('YYYY-MM') measures that month; omitted, the backend uses the
 * tenant's newest commission month.
 */
export function useBrokerTargets(
  companyId: string | undefined,
  brokerDocId: string | undefined,
  periodMonth?: string | null
): UseBrokerTargetsReturn {
  const [ownTargets, setOwnTargets] = useState<PerformanceTarget[]>([]);
  const [crossTargets, setCrossTargets] = useState<PerformanceTarget[]>([]);
  const [progress, setProgress] = useState<TargetProgress[]>([]);
  const [totalBonusPayableCents, setTotalBonusPayableCents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [progressLoading, setProgressLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [measuredMonth, setMeasuredMonth] = useState<string | null>(null);
  const [latestDataMonth, setLatestDataMonth] = useState<string | null>(null);
  const [progressError, setProgressError] = useState<string | null>(null);
  // Switching months quickly can resolve requests out of order; only the
  // newest one may write state.
  const requestSeq = useRef(0);

  // PR 1A-v5.1 (2026-05-15): merged view of own + cross-broker analyst-bound
  // targets. A credit analyst working multiple brokers should see her
  // target on EITHER broker's tab — without this, Hannah Ruby's target
  // (stored under Andrew) is invisible from Kelsey's tab.
  const targets = [...ownTargets, ...crossTargets];

  // Effect 1: Real-time Firestore listener on performance_targets subcollection (active only)
  useEffect(() => {
    if (!companyId || !brokerDocId) {
      setOwnTargets([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const colRef = collection(
      db,
      `companies/${companyId}/brokers/${brokerDocId}/performance_targets`
    );
    const q = query(colRef, where('isActive', '==', true));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const results: PerformanceTarget[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // Normalize legacy v2 types → commission_earned for the UI.
          // Keeps the rest of the frontend single-pathed.
          let type: TargetType = data.type;
          let commissionComponents: CommissionComponent[] | undefined = data.commissionComponents;
          if (type === 'upfront_commission') {
            type = 'commission_earned';
            commissionComponents = ['upfront'];
          } else if (type === 'trail_commission') {
            type = 'commission_earned';
            commissionComponents = ['trail'];
          }
          // Auto-migrate legacy single-bonus shape → tiered shape on read.
          // Existing pre-Phase-6 targets have only thresholdCents/bonusCents;
          // new targets have bonusTiers populated. Either way, the UI sees a
          // non-empty sorted array.
          const bonusTiers = normalizeBonusTiers(
            Number(data.thresholdCents) || 0,
            Number(data.bonusCents) || 0,
            data.bonusTiers as BonusTier[] | undefined,
          );
          results.push({
            id: docSnap.id,
            type,
            label: data.label,
            thresholdCents: bonusTiers[0].thresholdCents,
            bonusCents: bonusTiers[bonusTiers.length - 1].bonusCents,
            bonusTiers,
            period: data.period,
            isActive: data.isActive,
            createdAt: data.createdAt?.toDate?.() ?? new Date(0),
            createdBy: data.createdBy || '',
            updatedAt: data.updatedAt?.toDate?.() ?? new Date(0),
            commissionComponents,
            loanBookId: data.loanBookId,
            loanBookLabel: data.loanBookLabel,
            loanBookTemplateId: data.loanBookTemplateId,
            loanBookEntityCode: data.loanBookEntityCode,
            effectiveFromMonth: data.effectiveFromMonth,
            // PR 1A-v4 (2026-05-14): subject binding fields. Without these
            // in the enumeration the UI cannot render "(Hanah)" badges, and
            // the modal can't pre-populate the subject picker when editing
            // an analyst-bound target.
            assistantUserId: data.assistantUserId,
            assistantDisplayName: data.assistantDisplayName,
            analystId: data.analystId,
            analystDisplayName: data.analystDisplayName,
          });
        });
        setOwnTargets(results);
        setLoading(false);
      },
      (err) => {
        console.error('[useBrokerTargets] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load targets');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId, brokerDocId]);

  // Effect 1b (PR 1A-v5.1 — revised to v5.1.1): cross-broker analyst-target
  // synthesis from the progress callable response.
  //
  // Initial v5.1 implementation used a client-side collectionGroup query on
  // performance_targets — which requires a CG-scoped Firestore rule that
  // doesn't exist (rule is nested under /companies/{c}/brokers/{b}/...).
  // Adding the CG rule would require denormalizing companyId onto every
  // target doc + a backfill migration.
  //
  // Cleaner: the backend `getBrokerTargetProgress` callable (v5.1) already
  // pulls cross-broker analyst targets via Admin SDK (no rules) and returns
  // them in the progress list tagged with `_crossBrokerSource`. We can
  // synthesize PerformanceTarget objects from those progress entries —
  // every field the UI needs is on the progress entry (label, threshold,
  // tiers, period, components, loan book scoping, subject binding).
  //
  // Tradeoff: cross-broker targets refresh when refetchProgress fires (e.g.
  // mount, manual refresh, save callback) rather than via onSnapshot. For
  // a "shared target visible from a non-funding broker" view this is fine —
  // edits happen on the funding broker's page where onSnapshot still
  // applies, and the next progress fetch will pick up any change.
  useEffect(() => {
    const synthesized = progress
      .filter((p) => p._crossBrokerSource)
      .map<PerformanceTarget>((p) => {
        const tiers = Array.isArray(p.bonusTiers) && p.bonusTiers.length > 0
          ? p.bonusTiers
          : normalizeBonusTiers(p.thresholdCents, p.bonusCents, undefined);
        return {
          id: p.targetId,
          type: p.type as TargetType,
          label: p.label,
          thresholdCents: tiers[0].thresholdCents,
          bonusCents: tiers[tiers.length - 1].bonusCents,
          bonusTiers: tiers,
          period: p.period as 'monthly' | 'quarterly',
          isActive: true,
          createdAt: new Date(0),
          createdBy: '',
          updatedAt: new Date(0),
          commissionComponents: p.commissionComponents,
          loanBookId: p.loanBookId,
          loanBookLabel: p.loanBookLabel,
          loanBookTemplateId: p.loanBookTemplateId,
          loanBookEntityCode: p.loanBookEntityCode,
          effectiveFromMonth: p.effectiveFromMonth,
          assistantUserId: p.assistantUserId,
          assistantDisplayName: p.assistantDisplayName,
          analystId: p.analystId,
          analystDisplayName: p.analystDisplayName,
          _crossBrokerSource: p._crossBrokerSource,
        };
      });
    setCrossTargets(synthesized);
  }, [progress]);

  // Effect 2: Fetch progress from BigQuery callable
  const fetchProgress = useCallback(async () => {
    const seq = ++requestSeq.current;
    if (!companyId || !brokerDocId) {
      setProgress([]);
      setTotalBonusPayableCents(0);
      return;
    }
    setProgressLoading(true);
    setProgressError(null);
    try {
      const getProgress = httpsCallable(functions, 'getBrokerTargetProgress');
      const result = await getProgress({
        companyId,
        brokerDocId,
        ...(periodMonth ? { periodMonth } : {}),
      });
      if (seq !== requestSeq.current) return;
      const data = result.data as {
        progress?: TargetProgress[];
        totalBonusPayableCents?: number;
        measuredMonth?: string;
        latestDataMonth?: string | null;
      };
      setProgress(data.progress || []);
      setTotalBonusPayableCents(data.totalBonusPayableCents || 0);
      setMeasuredMonth(data.measuredMonth ?? null);
      setLatestDataMonth(data.latestDataMonth ?? null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.error('[useBrokerTargets] Progress fetch error:', err);
      // Non-fatal: targets still show from Firestore. Progress is cleared so a
      // failed month switch never leaves the previous month's numbers on screen
      // under the newly picked month.
      setProgress([]);
      setTotalBonusPayableCents(0);
      setMeasuredMonth(null);
      setProgressError(err instanceof Error ? err.message : 'Failed to load progress');
    } finally {
      if (seq === requestSeq.current) setProgressLoading(false);
    }
  }, [companyId, brokerDocId, periodMonth]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  return {
    targets,
    progress,
    totalBonusPayableCents,
    loading,
    progressLoading,
    error,
    refetchProgress: fetchProgress,
    measuredMonth,
    latestDataMonth,
    progressError,
  };
}
